import { MediaMatcher } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component } from '@angular/core';
import {
    BehaviorSubject,
    combineLatest,
    isObservable,
    Observable,
    of,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { takeUntil, filter } from 'rxjs/operators';
import { ActivatedRoute, Event, NavigationEnd } from '@angular/router';

import { AbstractBaseComponent } from '@elight/client/shared/utility/common';
import {
    AuthService,
    Division,
    RoleVoter,
    User,
} from '@elight/client/shared/utility/auth';
import { SidenavService } from '@elight/client/shared/utility/state';
import { Router } from '@angular/router';

interface IMenu {
    icon: string;
    title: string;
    isVisible: boolean | Observable<boolean>;
    route?: string;
    children?: IMenuChildren[];
    isActive?: boolean;
    path?: string;
}

interface IMenuChildren extends Omit<IMenu, 'children' | 'icon' | 'isActive'> {}

@Component({
    selector: 'elight-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent extends AbstractBaseComponent {
    public mediaQueryList: MediaQueryList;
    public user$: Observable<User>;
    public roleMapCanRead: {
        projects: boolean | Observable<boolean>;
        database: boolean | Observable<boolean>;
        users: boolean | Observable<boolean>;
        variables: boolean | Observable<boolean>;
        'proposals-templates': boolean | Observable<boolean>;
    };
    public divisionChangeInProgress = false;

    public readonly menu: IMenu[];

    private readonly mobileQueryListener: () => void;

    constructor(
        private authService: AuthService,
        private router: Router,
        private roleVoter: RoleVoter,
        private changeDetectorRef: ChangeDetectorRef,
        private mediaMatcher: MediaMatcher,
        private sideNavService: SidenavService,
        private activatedRoute: ActivatedRoute
    ) {
        super();
        this.roleMapCanRead = {
            projects: true,
            database: true,
            users: true,
            variables: true,
            'proposals-templates': true,
        };

        this.router.config.forEach((item) => {
            if (this.roleMapCanRead[item.path]) {
                this.roleMapCanRead[item.path] = this.roleVoter.hasAccess(
                    item?.data?.roles?.read
                );
            }
        });

        this.mobileQueryListener = () => changeDetectorRef.detectChanges();
        this.mediaQueryList = mediaMatcher.matchMedia('(max-width: 600px)');
        this.mediaQueryList.addEventListener(
            'change',
            this.mobileQueryListener
        );

        this.user$ = this.authService.currentUser$.pipe(
            takeUntil(this.destroy$)
        );

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
                this.sidebarToggle();
                this.setActiveNvItems();
            });

        this.menu = [
            {
                icon: 'folder',
                title: 'Projects',
                route: '/projects',
                isVisible: this.roleMapCanRead.projects,
            },
            {
                icon: 'topic_outline',
                title: 'Database',
                isVisible: true,
                isActive: false,
                path: '/database',
                children: [
                    {
                        title: 'Existing Luminaires',
                        route: '/database/existing',
                        isVisible: this.roleMapCanRead.database,
                    },
                    {
                        title: 'Luminaire Specification',
                        route: '/database/luminaire-specification',
                        isVisible: this.roleMapCanRead.database,
                    },
                    {
                        title: 'Installation Details',
                        route: '/database/installation-details',
                        isVisible: this.roleMapCanRead.database,
                    },
                    {
                        title: 'Manufacturers',
                        route: '/database/manufacturers',
                        isVisible: this.roleMapCanRead.database,
                    },
                    {
                        title: 'Proposed Luminaires',
                        route: '/database/proposed',
                        isVisible: this.roleMapCanRead.database,
                    },
                    {
                        title: 'Sensors',
                        route: '/database/sensors',
                        isVisible: this.roleMapCanRead.database,
                    },
                ],
            },
            {
                icon: 'calculate',
                title: 'Variables',
                route: '/variables',
                isVisible: this.roleMapCanRead.variables,
            },
            {
                icon: 'feed',
                title: 'Proposals Templates',
                route: '/proposals-templates',
                isVisible: this.isPTVisible(),
            },
            {
                icon: 'people',
                title: 'Users',
                route: '/users',
                isVisible: this.roleMapCanRead.users,
            },
        ];
    }

    public logout() {
        this.authService.logout().subscribe(() => {
            this.router.navigate(['auth/login']);
        });
    }

    public changeDivision(division: Division) {
        this.divisionChangeInProgress = true;
        this.authService.changeDivision(division).subscribe(() => {
            this.divisionChangeInProgress = false;
        });
    }

    get navOpen$(): Observable<boolean> {
        return this.sideNavService.isOpen$;
    }

    public makeOf(value: boolean | Observable<boolean>) {
        return isObservable(value) ? value : of(value);
    }

    private sidebarToggle() {
        const currentRoute = this.getActiveRoute(this.activatedRoute);
        const showSidebar = currentRoute.snapshot?.data?.showSidebar ?? true;

        this.sideNavService.toggle(showSidebar);
    }

    private setActiveNvItems() {
        this.menu.forEach((item) => {
            if (item.isActive !== undefined) {
                item.isActive = this.router.url.startsWith(item.path);
            }
        });
    }

    private isPTVisible(): Observable<boolean> {
        const role$ = this.makeOf(this.roleMapCanRead['proposals-templates']);

        return combineLatest([this.user$, role$]).pipe(
            map(([user, canRead]) => null !== user.currentDivisionId && canRead)
        );
    }

    private getActiveRoute(route: ActivatedRoute): ActivatedRoute {
        let activeRoute = route;

        while (activeRoute.firstChild) {
            activeRoute = activeRoute.firstChild;
        }

        return activeRoute;
    }
}
